import { observable } from '@legendapp/state';
import { enableReactTracking } from '@legendapp/state/config/enableReactTracking';
import { enableReactUse } from '@legendapp/state/config/enableReactUse';
import { logger } from '@/lib/utils/logger';
import { featureFlagsService } from '@/lib/services';
import { Session } from 'next-auth';

const isClient = typeof window !== 'undefined';

if (isClient) {
  enableReactTracking({ auto: true });
  enableReactUse();
}

/**
 * Represents the expected shape of the feature flag DTO returned by the backend Admin API.
 * This is used for type safety when interacting with the service.
 */
type AdminFeatureFlagDto = FeatureFlag & {
  name: string;
  description?: string | null;
};

/**
 * Defines the structure for a single feature flag within the store.
 */
export interface FeatureFlag {
  enabled: boolean;
  description?: string | null;
}

/**
 * Defines the structure for the feature flags state managed by Legend State.
 */
interface FeatureFlagsState {
  /** A record mapping feature flag names (string) to their FeatureFlag object. */
  flags: Record<string, FeatureFlag>;
  /** Indicates if the flags are currently being initialized or fetched via the service. */
  isLoading: boolean;
  /** Stores any error encountered during initialization (primarily set by the service). */
  error: Error | null;
  /** Tracks the loading and error status for individual flag CUD operations, keyed by flag name. */
  operationStatus: Record<
    string,
    {
      type: 'create' | 'update' | 'delete';
      loading: boolean;
      error: string | null;
    }
  >;
}

const initialState: FeatureFlagsState = {
  flags: {},
  isLoading: true, // Start in loading state until initialization completes
  error: null,
  operationStatus: {},
};

/**
 * Observable state object for managing feature flags using Legend State.
 */
export const featureFlagsState = observable<FeatureFlagsState>(initialState);

// Helper to set operation status
const setOpStatus = (
  flagName: string,
  type: 'create' | 'update' | 'delete',
  loading: boolean,
  error: string | null
) => {
  if (!featureFlagsState.operationStatus[flagName].peek()) {
    featureFlagsState.operationStatus[flagName].set({ type, loading, error });
  } else {
    featureFlagsState.operationStatus[flagName].assign({
      type,
      loading,
      error,
    });
  }
};

// Helper to clear operation status
const clearOpStatus = (flagName: string) => {
  featureFlagsState.operationStatus[flagName].delete();
};

/**
 * Provides actions to interact with the feature flags state.
 */
export const featureFlagsActions = {
  /**
   * Initiates the process of loading feature flags by calling the FeatureFlagsService.
   * The service handles fetching, caching, and updating the store's loading/error/flags state.
   * Requires a valid session.
   *
   * @param session - The user's current session, or null if not authenticated.
   * @throws Re-throws errors from the service layer.
   */
  initializeFlags: async (session: Session | null): Promise<void> => {
    logger.debug('[featureFlagsStore] Initialize flags action triggered.');
    // Explicitly set loading to true at the start of the action
    if (!featureFlagsState.isLoading.peek()) {
      featureFlagsState.isLoading.set(true);
    }
    featureFlagsState.error.set(null);

    try {
      await featureFlagsService.initializeFlags(session);
      logger.debug('[featureFlagsStore] Service initializeFlags completed.');
    } catch (error: any) {
      logger.error(
        '[featureFlagsStore] Error during featureFlagsService.initializeFlags call',
        error
      );

      if (!featureFlagsState.error.peek()) {
        featureFlagsState.error.set(
          error instanceof Error
            ? error
            : new Error('Failed to initialize flags via service')
        );
      }
    }

    featureFlagsState.isLoading.set(false);
    logger.debug(
      '[featureFlagsStore] Initialize flags action finished, isLoading set to false.'
    );
  },

  /**
   * Creates a new feature flag optimistically and via the backend service.
   * Reverts the local state if the backend creation fails.
   * Requires a valid session with admin privileges.
   *
   * @param flagData - Data for the new flag (name, enabled, description).
   * @param session - The user's current session.
   * @returns A promise resolving to the created flag data from the API, or rejecting on failure.
   * @throws Re-throws errors from the service layer or validation.
   */
  createFlag: async ({
    flagData,
    session,
  }: {
    flagData: { name: string; description?: string; enabled?: boolean };
    session: Session;
  }): Promise<AdminFeatureFlagDto> => {
    const { name, description, enabled } = flagData;
    logger.debug(
      `[featureFlagsStore] createFlag action: Attempting optimistic create for '${name}'`
    );

    // Check if flag already exists locally (should ideally be prevented by UI)
    if (featureFlagsState.flags[name].peek()) {
      const errorMsg = `Flag '${name}' already exists locally.`;
      logger.warn(`[featureFlagsStore] createFlag action: ${errorMsg}`);
      setOpStatus(name, 'create', false, errorMsg);
      throw new Error(errorMsg);
    }

    // Set initial loading status
    setOpStatus(name, 'create', true, null);

    // Optimistic UI update: Create the flag locally
    const newFlag: FeatureFlag = {
      enabled: enabled ?? false,
      // description: description ?? null, // Add if description is part of FeatureFlag interface
    };
    featureFlagsState.flags[name].set(newFlag);

    try {
      // Call the service to create the flag on the backend
      const createdFlag = await featureFlagsService.createFlag(
        flagData,
        session
      );
      logger.info(
        `[featureFlagsStore] createFlag action: Service create successful for '${name}'.`
      );
      // Update local flag with potentially more complete data from backend (e.g., description)
      // Note: Only update if the backend data differs significantly or includes fields not set optimistically
      // featureFlagsState.flags[name].set({ enabled: createdFlag.enabled /*, description: createdFlag.description */ });

      // Clear operation status on success
      clearOpStatus(name);
      return createdFlag; // Return data from backend
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Create failed on server';
      logger.error(
        `[featureFlagsStore] createFlag action: Service create failed for '${name}'. Reverting optimistic create. Error: ${errorMessage}`,
        error
      );
      // Set error status
      setOpStatus(name, 'create', false, errorMessage);

      // Revert the optimistic create
      logger.debug(
        `[featureFlagsStore] Reverting optimistic create for '${name}'.`
      );
      featureFlagsState.flags[name].delete();
      // Keep error status set for the UI to display

      throw error; // Re-throw for the caller
    }
  },

  /**
   * Updates a feature flag's state (enabled/description) optimistically and via the backend service.
   * Reverts the local state if the backend update fails.
   * Manages the specific `operationStatus` for the flag during the operation.
   * Requires a valid session with admin privileges.
   *
   * @param flagName - The name of the feature flag to update.
   * @param updateData - An object containing the properties to update (e.g., { enabled: boolean, description?: string }).
   * @param session - The user's current session.
   * @returns A promise resolving to the updated flag data from the API, or rejecting on failure.
   * @throws Re-throws errors from the service layer or validation.
   */
  updateFlag: async ({
    flagName,
    updateData,
    session,
  }: {
    flagName: string;
    updateData: { enabled?: boolean; description?: string };
    session: Session;
  }): Promise<AdminFeatureFlagDto> => {
    const flags = featureFlagsState.flags;
    const originalFlag = flags[flagName]?.peek();

    if (!originalFlag) {
      const errorMsg = `Flag '${flagName}' not found locally for update.`;
      logger.error(`[featureFlagsStore] updateFlag action: ${errorMsg}`);
      setOpStatus(flagName, 'update', false, errorMsg);
      throw new Error(errorMsg);
    }

    logger.debug(
      `[featureFlagsStore] updateFlag action: Attempting optimistic update for '${flagName}'`,
      updateData
    );

    setOpStatus(flagName, 'update', true, null);

    // Perform optimistic UI update
    // Create a temporary updated flag object for the update
    const updatedFlagPartial: Partial<FeatureFlag> = {};
    if (updateData.enabled !== undefined) {
      updatedFlagPartial.enabled = updateData.enabled;
    }
    // if (updateData.description !== undefined) { // If handling description
    //   updatedFlagPartial.description = updateData.description;
    // }
    flags[flagName].assign(updatedFlagPartial);

    try {
      const updatedFlag = await featureFlagsService.updateFlag(
        { flagName, updateData },
        session
      );

      logger.info(
        `[featureFlagsStore] updateFlag action: Service update successful for '${flagName}'.`
      );
      // Optionally update with full data from backend if necessary
      // flags[flagName].set({ enabled: updatedFlag.enabled, description: updatedFlag.description });

      clearOpStatus(flagName);
      return updatedFlag;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Update failed on server';
      logger.error(
        `[featureFlagsStore] updateFlag action: Service update failed for '${flagName}'. Reverting optimistic update. Error: ${errorMessage}`,
        error
      );
      setOpStatus(flagName, 'update', false, errorMessage);

      // Revert the optimistic update
      logger.debug(
        `[featureFlagsStore] Reverting optimistic update for '${flagName}'.`
      );
      flags[flagName].set(originalFlag); // Revert to original state
      // Keep error status set

      throw error; // Re-throw for the caller
    }
  },

  /**
   * Deletes a feature flag optimistically and via the backend service.
   * Reverts the local state if the backend deletion fails.
   * Requires a valid session with admin privileges.
   *
   * @param flagName - The name of the feature flag to delete.
   * @param session - The user's current session.
   * @returns A promise resolving on successful API deletion or rejecting on failure.
   * @throws Re-throws errors from the service layer.
   */
  deleteFlag: async ({
    flagName,
    session,
  }: {
    flagName: string;
    session: Session;
  }): Promise<void> => {
    const flags = featureFlagsState.flags;
    const originalFlag = flags[flagName]?.peek();

    if (!originalFlag) {
      const errorMsg = `Flag '${flagName}' not found locally for deletion.`;
      logger.warn(`[featureFlagsStore] deleteFlag action: ${errorMsg}`);
      // No need to set status if it doesn't exist; maybe clear any lingering status?
      clearOpStatus(flagName);
      return; // Or throw? Let's just return silently if not found locally.
    }

    logger.debug(
      `[featureFlagsStore] deleteFlag action: Attempting optimistic delete for '${flagName}'`
    );

    setOpStatus(flagName, 'delete', true, null);

    // Optimistic UI update: Remove the flag locally
    flags[flagName].delete();

    try {
      // Call the service to delete the flag on the backend
      await featureFlagsService.deleteFlag(flagName, session);
      logger.info(
        `[featureFlagsStore] deleteFlag action: Service delete successful for '${flagName}'.`
      );
      // Clear status on success
      clearOpStatus(flagName);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Delete failed on server';
      logger.error(
        `[featureFlagsStore] deleteFlag action: Service delete failed for '${flagName}'. Reverting optimistic delete. Error: ${errorMessage}`,
        error
      );
      // Set error status for the flag that failed to delete
      setOpStatus(flagName, 'delete', false, errorMessage);

      // Revert the optimistic delete by restoring the original flag
      logger.debug(
        `[featureFlagsStore] Reverting optimistic delete for '${flagName}'.`
      );
      flags[flagName].set(originalFlag);
      // Keep error status set

      throw error; // Re-throw for the caller
    }
  },

  /**
   * Replaces the entire set of flags in the store.
   * Typically used internally by the FeatureFlagsService after fetching flags.
   * Clears any existing individual flag operation statuses.
   *
   * @param flags - The complete set of flags to store.
   */
  setFlags: ({ flags }: { flags: Record<string, FeatureFlag> }) => {
    logger.debug(
      `[featureFlagsStore] setFlags action: Replacing all flags. Count: ${Object.keys(flags).length}`
    );
    // Clear previous operation statuses when resetting all flags
    featureFlagsState.operationStatus.set({}); // Changed from updateStatus
    featureFlagsState.flags.set(flags);
  },
};

/**
 * Provides selectors to read data from the feature flags state.
 */
export const featureFlagsSelectors = {
  /**
   * Checks if a specific feature flag is enabled.
   * Returns false if flags are loading, an error occurred during init,
   * or the flag doesn't exist/is explicitly disabled.
   *
   * @param flagName - The name of the feature flag.
   * @returns True if the feature is enabled, false otherwise.
   */
  isFeatureEnabled: (flagName: string): boolean => {
    const state = featureFlagsState.get();

    // Prioritize loading and error states
    if (state.isLoading) {
      // logger.debug(`[featureFlagsStore] isFeatureEnabled('${flagName}'): Still loading, returning false.`);
      return false;
    }
    if (state.error) {
      logger.warn(
        `[featureFlagsStore] isFeatureEnabled('${flagName}'): Global error state set, returning false.`
      );
      return false;
    }

    // Check the actual flag state
    const isEnabled = state.flags[flagName]?.enabled ?? false;
    // console.log(
    //   `[featureFlagsStore] isFeatureEnabled('${flagName}'): Result = ${isEnabled}`
    // );
    return isEnabled;
  },

  getFlag: (flagName: string): FeatureFlag | undefined => {
    return featureFlagsState.flags[flagName]?.get();
  },

  /**
   * Gets the entire map of feature flags.
   * @returns A read-only record of all feature flags and their states.
   */
  getAllFlags: (): Readonly<Record<string, FeatureFlag>> => {
    return featureFlagsState.flags.get();
  },

  /**
   * Gets the overall loading state related to flag initialization.
   * @returns True if flags are currently being loaded/initialized by the service.
   */
  isLoading: (): boolean => {
    return featureFlagsState.isLoading.get();
  },

  /**
   * Gets the overall error state related to flag initialization.
   * @returns An Error object if initialization failed, otherwise null.
   */
  getError: (): Error | null => {
    return featureFlagsState.error.get();
  },

  /**
   * Gets the specific operation status (type, loading, error) for a single flag modification attempt.
   *
   * @param flagName - The name of the feature flag.
   * @returns An object with `{ type: 'create' | 'update' | 'delete'; loading: boolean; error: string | null }` or a default status.
   */
  getFlagOperationStatus: (
    flagName: string
  ): {
    type: 'create' | 'update' | 'delete';
    loading: boolean;
    error: string | null;
  } => {
    return (
      featureFlagsState.operationStatus[flagName].get() ?? {
        type: 'update',
        loading: false,
        error: null,
      }
    ); // Default to type update?
  },
};

// Initialization is now handled explicitly via featureFlagsActions.initializeFlags()
// triggered from appropriate places in the application lifecycle (e.g., providers, dashboards).
